import { onMounted, onUnmounted } from 'vue'

import { useEnv } from '@/shared/config'

// Ports the v3 `App.vue:34–59` version gate (contract 05 §3, §14; ORP-18612): a session that is
// still running the previous build asks the server which build is current, on mount and every
// time the tab becomes visible again, and reloads itself when a deploy has landed underneath it.
//
// Server contract (ORP-19067 / BE-PLT-01): the manifest is `{"version": "<build id>"}` served
// `Cache-Control: no-store`. The cache-busting query stays from v3 because the no-store header
// only binds the origin, not an intermediary that might still hand back a cached body.
//
// The gate is silent by design — it never renders, so it owns no copy.

/** Deploy manifest, emitted by the build (`vite.config.ts`) and served by `deploy/nginx.conf`.
 *
 *  It lives under the v4 bundle prefix rather than at the root because the strangler cutover puts
 *  v3 and v4 on one origin split by path, with `/` belonging to v3 (ledger B21): a request for
 *  `/version.json` would be answered by v3's SPA fallback with 200 + `index.html`, `.json()` would
 *  throw, and the gate would be dead — which is precisely the v3 defect ORP-18562 documented.
 *  `/v4-assets/` already routes to v4, so this path is reachable with no extra ingress rule. */
const VERSION_URL = '/v4-assets/version.json'

/** Handshake with the boot that follows the reload (`useBootstrap`), which upgrades it to
 *  `boot(true)`. Also read by the still-live v3 app (`app/boot/bootUser.ts:20–23`), which shares
 *  this origin's storage during the strangler overlap — the key name is that cross-app contract. */
export const REFRESH_ALL_KEY = 'refreshAll'

/** Per-tab record of the server version this tab already reloaded for. */
const RELOADED_FOR_KEY = 'orphex:versionGate:reloadedFor'

/** The server build id, or `undefined` for any payload that is not the agreed `{version: string}`.
 *  Fails closed: an unreadable payload must not read as "different from ours" and trigger a
 *  reload, which is how a bad deploy would otherwise reload every tab forever. */
function readServerVersion(payload: unknown): string | undefined {
  if (typeof payload !== 'object' || payload === null) return undefined
  const { version } = payload as { version?: unknown }
  return typeof version === 'string' && version !== '' ? version : undefined
}

/**
 * Reload the tab when the deployed build no longer matches this one. No-ops entirely unless
 * `APP_VERSION_KEY` is configured, so dev/test/unversioned builds never fetch or reload.
 */
export function useVersionGate(): void {
  const currentVersion = useEnv().APP_VERSION_KEY
  if (currentVersion === undefined) return

  const check = async (): Promise<void> => {
    try {
      const response = await fetch(`${VERSION_URL}?t=${Date.now()}`)
      if (!response.ok) return

      const serverVersion = readServerVersion(await response.json())
      if (serverVersion === undefined || serverVersion === currentVersion) return

      // A reload only helps if it can actually swap the bundle. Having already reloaded for this
      // exact server version and still booted this one, reloading again would repeat verbatim —
      // the build cannot be picked up (a cached index.html, or a bundle whose APP_VERSION_KEY can
      // never equal what the server reports). Stop: a stale bundle is usable, a reload loop is not.
      if (sessionStorage.getItem(RELOADED_FOR_KEY) === serverVersion) return
      sessionStorage.setItem(RELOADED_FOR_KEY, serverVersion)

      localStorage.setItem(REFRESH_ALL_KEY, 'true')
      window.location.reload()
    } catch (error) {
      // Offline, blocked, or non-JSON: keep the session on its working bundle.
      console.error('[orphex] version check failed:', error)
    }
  }

  const handleVisibilityChange = (): void => {
    if (!document.hidden) void check()
  }

  onMounted(() => {
    document.addEventListener('visibilitychange', handleVisibilityChange)
    void check()
  })

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', handleVisibilityChange)
  })
}
